import MuiButton from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { styled } from '@mui/system'
import { StalePageInfo } from '../../hooks/editingState'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'

type StaleWarningDialogProps = {
  isOpen: boolean
  stalePages: StalePageInfo[]
  onProceedWithCurrentData: () => void
  onProceedWithLatestData: () => void
  onClose: () => void
}

export const StaleWarningDialog = ({
  isOpen,
  stalePages,
  onProceedWithCurrentData,
  onProceedWithLatestData,
  onClose,
}: StaleWarningDialogProps) => {
  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>
        {intl.formatMessage({ id: 'versionReport.staleWarning.title' })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {intl.formatMessage({ id: 'versionReport.staleWarning.message' })}
        </DialogContentText>
        <StalePageList>
          {stalePages.map(page => (
            <StalePageItem key={page.uuid}>
              {intl.formatMessage(
                { id: 'versionReport.staleWarning.pageLabel' },
                { pageNumber: page.pageNumber, pageTitle: page.pageTitle }
              )}
            </StalePageItem>
          ))}
        </StalePageList>
        <DialogContentText sx={{ marginTop: 2 }}>
          {intl.formatMessage({ id: 'versionReport.staleWarning.question' })}
        </DialogContentText>
      </DialogContent>
      <ButtonContainer>
        <PrimaryActionButton onClick={onProceedWithLatestData}>
          {intl.formatMessage({
            id: 'versionReport.staleWarning.proceedWithLatest',
          })}
        </PrimaryActionButton>
        <SecondaryActionButton onClick={onProceedWithCurrentData}>
          {intl.formatMessage({
            id: 'versionReport.staleWarning.proceedWithCurrent',
          })}
        </SecondaryActionButton>
        <CancelButton onClick={onClose}>
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </CancelButton>
      </ButtonContainer>
    </Dialog>
  )
}

const StalePageList = styled('ul')({
  margin: '12px 0',
  paddingLeft: '20px',
  backgroundColor: colorPalette.monotone[1],
  borderRadius: '4px',
  padding: '12px 12px 12px 32px',
})

const StalePageItem = styled('li')({
  color: colorPalette.monotone[10],
  fontSize: '14px',
  marginBottom: '4px',
  '&:last-child': {
    marginBottom: 0,
  },
})

const ButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 24px',
  gap: '8px',
})

const ActionButton = styled(MuiButton)({
  textTransform: 'none',
  width: '100%',
})

const PrimaryActionButton = styled(ActionButton)({
  backgroundColor: colorPalette.skyBlue[7],
  color: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.skyBlue[7]}`,
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[5],
    border: `1px solid ${colorPalette.skyBlue[5]}`,
  },
})

const SecondaryActionButton = styled(ActionButton)({
  border: `1px solid ${colorPalette.skyBlue[7]}`,
  color: colorPalette.skyBlue[7],
  '&:hover': {
    border: `1px solid ${colorPalette.skyBlue[4]}`,
    color: colorPalette.skyBlue[4],
    backgroundColor: 'transparent',
  },
})

const CancelButton = styled(MuiButton)({
  textTransform: 'none',
  marginTop: '8px',
})
